import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import {
  Video,
  Plus,
  Trash2,
  Pencil,
  Play,
  ExternalLink,
  Sparkles,
  Loader2,
  Wand2,
} from "lucide-react";
import type { MarketingVideo } from "@shared/schema";

function extractErrorMessage(err: any, fallback: string): string {
  const msg = err?.message || "";
  const jsonMatch = msg.match(/\d+:\s*(\{.*\})/);
  if (jsonMatch) {
    try {
      const parsed = JSON.parse(jsonMatch[1]);
      return parsed.message || fallback;
    } catch {}
  }
  return msg || fallback;
}

const CATEGORIES = [
  { value: "how-it-works", label: "How It Works" },
  { value: "feature-announcement", label: "Feature Announcement" },
  { value: "campaign", label: "Campaign" },
  { value: "testimonial", label: "Testimonial" },
  { value: "seasonal", label: "Seasonal" },
];

function VideoForm({
  video,
  onClose,
}: {
  video?: MarketingVideo;
  onClose: () => void;
}) {
  const { toast } = useToast();
  const isEditing = !!video;

  const [aiPrompt, setAiPrompt] = useState("");
  const [videoPrompt, setVideoPrompt] = useState("");
  const [title, setTitle] = useState(video?.title || "");
  const [description, setDescription] = useState(video?.description || "");
  const [videoUrl, setVideoUrl] = useState(video?.videoUrl || "");
  const [thumbnailUrl, setThumbnailUrl] = useState(video?.thumbnailUrl || "");
  const [category, setCategory] = useState(video?.category || "how-it-works");
  const [ctaText, setCtaText] = useState(video?.ctaText || "");
  const [ctaLink, setCtaLink] = useState(video?.ctaLink || "");
  const [durationSeconds, setDurationSeconds] = useState(video?.durationSeconds?.toString() || "");
  const aiContentMutation = useMutation({
    mutationFn: async (data: { prompt: string; category: string }) => {
      const res = await apiRequest("POST", "/api/admin/marketing-videos/ai-generate", data);
      return res.json();
    },
    onSuccess: (data: any) => {
      setTitle(data.title || "");
      setDescription(data.description || "");
      setCtaText(data.ctaText || "");
      setCtaLink(data.ctaLink || "");
      setVideoPrompt(data.videoPrompt || "");
      setDurationSeconds(data.durationSeconds?.toString() || "8");
      toast({ title: "Content generated", description: "Review and adjust the fields below, then generate a video or add a URL." });
    },
    onError: (err: any) => {
      toast({ title: "Generation failed", description: extractErrorMessage(err, "Could not generate content"), variant: "destructive" });
    },
  });

  const aiVideoMutation = useMutation({
    mutationFn: async (data: { videoPrompt: string; durationSeconds: number }) => {
      const res = await apiRequest("POST", "/api/admin/marketing-videos/ai-generate-video", data);
      return res.json();
    },
    onSuccess: (data: any) => {
      if (data.thumbnailUrl) {
        setThumbnailUrl(data.thumbnailUrl);
      }
      if (data.durationSeconds) {
        setDurationSeconds(data.durationSeconds.toString());
      }
      toast({ title: "Thumbnail generated", description: "A poster image was created from your video description. Upload your video to the public/videos/ folder." });
    },
    onError: (err: any) => {
      toast({ title: "Generation failed", description: extractErrorMessage(err, "Could not generate thumbnail"), variant: "destructive" });
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await apiRequest("POST", "/api/admin/marketing-videos", data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/marketing-videos"] });
      queryClient.invalidateQueries({ queryKey: ["/api/marketing-videos"] });
      toast({ title: "Video added" });
      onClose();
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await apiRequest("PUT", `/api/admin/marketing-videos/${video!.id}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/marketing-videos"] });
      queryClient.invalidateQueries({ queryKey: ["/api/marketing-videos"] });
      toast({ title: "Video updated" });
      onClose();
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const handleAiGenerate = () => {
    if (!aiPrompt.trim()) {
      toast({ title: "Enter a topic", description: "Describe what the video should be about", variant: "destructive" });
      return;
    }
    aiContentMutation.mutate({ prompt: aiPrompt.trim(), category });
  };

  const handleVideoGenerate = () => {
    if (!videoPrompt.trim()) {
      toast({ title: "No video prompt", description: "Generate content first or enter a video description", variant: "destructive" });
      return;
    }
    aiVideoMutation.mutate({
      videoPrompt: videoPrompt.trim(),
      durationSeconds: parseInt(durationSeconds) || 8,
    });
  };

  const TEST_TITLE_RE = /(pw\s*test|playwright|e2e\s*test)/i;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (title.trim().length < 3) {
      toast({ title: "Title too short", description: "Title must be at least 3 characters.", variant: "destructive" });
      return;
    }
    if (TEST_TITLE_RE.test(title)) {
      toast({ title: "Invalid title", description: "Title cannot contain test identifiers (PW Test, Playwright, E2E Test).", variant: "destructive" });
      return;
    }

    const data = {
      title,
      description: description || null,
      videoUrl,
      thumbnailUrl: thumbnailUrl || null,
      category,
      ctaText: ctaText || null,
      ctaLink: ctaLink || null,
      durationSeconds: durationSeconds ? parseInt(durationSeconds) : null,
    };

    if (isEditing) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;
  const isGenerating = aiContentMutation.isPending;
  const isVideoGenerating = aiVideoMutation.isPending;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {!isEditing && (
        <div className="rounded-md border border-primary/20 bg-primary/5 p-3 space-y-3">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Sparkles className="w-4 h-4 text-primary" />
            AI-Assisted Creation
          </div>
          <div className="space-y-2">
            <Label htmlFor="aiPrompt" className="text-xs text-muted-foreground">
              Describe your video topic and AI will fill everything in
            </Label>
            <div className="flex gap-2">
              <Input
                id="aiPrompt"
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                placeholder="e.g. How families can pool funds for a child's surgery"
                disabled={isGenerating}
                data-testid="input-ai-prompt"
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAiGenerate(); } }}
              />
              <Button
                type="button"
                size="default"
                onClick={handleAiGenerate}
                disabled={isGenerating || !aiPrompt.trim()}
                data-testid="button-ai-generate"
              >
                {isGenerating ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Wand2 className="w-4 h-4" />
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="title">Title</Label>
        <Input
          id="title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. How HealthWallet Works"
          required
          data-testid="input-video-title"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Brief description of the video"
          rows={2}
          data-testid="input-video-description"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="videoUrl">Video URL</Label>
        <div className="flex gap-2">
          <Input
            id="videoUrl"
            value={videoUrl}
            onChange={(e) => setVideoUrl(e.target.value)}
            placeholder="/videos/my-video.mp4 or https://..."
            required
            className="flex-1"
            data-testid="input-video-url"
          />
          {!isEditing && videoPrompt && (
            <Button
              type="button"
              variant="outline"
              size="default"
              onClick={handleVideoGenerate}
              disabled={isVideoGenerating}
              data-testid="button-ai-generate-video"
            >
              {isVideoGenerating ? (
                <Loader2 className="w-4 h-4 animate-spin mr-1" />
              ) : (
                <Sparkles className="w-4 h-4 mr-1" />
              )}
              {isVideoGenerating ? "Generating..." : "Generate Thumbnail"}
            </Button>
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          Upload videos to the public/videos/ folder, then reference as /videos/filename.mp4
        </p>
      </div>
      {videoPrompt && (
        <div className="space-y-2">
          <Label htmlFor="videoPrompt" className="text-xs">Video Generation Prompt</Label>
          <Textarea
            id="videoPrompt"
            value={videoPrompt}
            onChange={(e) => setVideoPrompt(e.target.value)}
            placeholder="AI-generated video scene description"
            rows={2}
            className="text-xs"
            data-testid="input-video-prompt"
          />
        </div>
      )}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="category">Category</Label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger data-testid="select-video-category">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CATEGORIES.map(c => (
                <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="duration">Duration (seconds)</Label>
          <Input
            id="duration"
            type="number"
            value={durationSeconds}
            onChange={(e) => setDurationSeconds(e.target.value)}
            placeholder="8"
            data-testid="input-video-duration"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="ctaText">CTA Button Text</Label>
          <Input
            id="ctaText"
            value={ctaText}
            onChange={(e) => setCtaText(e.target.value)}
            placeholder="e.g. Get Started"
            data-testid="input-video-cta-text"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="ctaLink">CTA Link</Label>
          <Input
            id="ctaLink"
            value={ctaLink}
            onChange={(e) => setCtaLink(e.target.value)}
            placeholder="/auth or /conditions"
            data-testid="input-video-cta-link"
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="thumbnailUrl">Thumbnail URL (optional)</Label>
        <Input
          id="thumbnailUrl"
          value={thumbnailUrl}
          onChange={(e) => setThumbnailUrl(e.target.value)}
          placeholder="/images/thumbnail.jpg"
          data-testid="input-video-thumbnail"
        />
      </div>
      <DialogFooter>
        <DialogClose asChild>
          <Button type="button" variant="outline">Cancel</Button>
        </DialogClose>
        <Button type="submit" disabled={isPending} data-testid="button-save-video">
          {isPending ? "Saving..." : isEditing ? "Update Video" : "Add Video"}
        </Button>
      </DialogFooter>
    </form>
  );
}

export function MarketingStudioDashboard() {
  const { toast } = useToast();
  const [editingVideo, setEditingVideo] = useState<MarketingVideo | undefined>();
  const [showForm, setShowForm] = useState(false);
  const [previewVideo, setPreviewVideo] = useState<MarketingVideo | null>(null);

  const { data: videos, isLoading } = useQuery<MarketingVideo[]>({
    queryKey: ["/api/admin/marketing-videos"],
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      const res = await apiRequest("PUT", `/api/admin/marketing-videos/${id}`, { isActive });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/marketing-videos"] });
      queryClient.invalidateQueries({ queryKey: ["/api/marketing-videos"] });
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err.message || "Failed to toggle video", variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/admin/marketing-videos/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/marketing-videos"] });
      queryClient.invalidateQueries({ queryKey: ["/api/marketing-videos"] });
      toast({ title: "Video deleted" });
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err.message || "Failed to delete video", variant: "destructive" });
    },
  });

  const activeCount = videos?.filter(v => v.isActive).length || 0;
  const totalCount = videos?.length || 0;

  return (
    <div className="space-y-6" data-testid="marketing-studio">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Video className="w-5 h-5 text-primary" />
            Marketing Studio
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Manage promotional videos displayed on the landing page
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant="secondary">{activeCount} active / {totalCount} total</Badge>
          <Dialog open={showForm && !editingVideo} onOpenChange={(open) => { setShowForm(open); if (!open) setEditingVideo(undefined); }}>
            <DialogTrigger asChild>
              <Button size="sm" data-testid="button-add-video">
                <Plus className="w-4 h-4 mr-2" />
                Add Video
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Add Marketing Video</DialogTitle>
              </DialogHeader>
              <VideoForm onClose={() => setShowForm(false)} />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2">
          {[1, 2].map(i => (
            <Card key={i}><CardContent className="pt-6 h-48 animate-pulse bg-muted/30" /></Card>
          ))}
        </div>
      ) : !videos?.length ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Video className="w-12 h-12 mx-auto text-muted-foreground/40 mb-4" />
            <h3 className="font-medium mb-2">No videos yet</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Add your first marketing video to showcase on the landing page
            </p>
            <Button size="sm" onClick={() => setShowForm(true)} data-testid="button-add-first-video">
              <Plus className="w-4 h-4 mr-2" />
              Add Video
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {videos.map((video) => (
            <Card key={video.id} className={!video.isActive ? "opacity-60" : ""} data-testid={`card-video-${video.id}`}>
              <CardContent className="pt-4">
                <div className="flex items-start gap-3">
                  <div className="shrink-0 w-28 h-20 rounded-md bg-muted/50 overflow-hidden relative group">
                    {video.videoUrl && (
                      <video
                        className="w-full h-full object-cover"
                        src={video.videoUrl}
                        muted
                        preload="metadata"
                      />
                    )}
                    <button
                      className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={() => setPreviewVideo(video)}
                      data-testid={`button-preview-video-${video.id}`}
                    >
                      <Play className="w-6 h-6 text-white" />
                    </button>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <h3 className="font-medium text-sm truncate" data-testid={`text-video-title-${video.id}`}>
                          {video.title}
                        </h3>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <Badge variant="outline" className="text-[10px]">
                            {CATEGORIES.find(c => c.value === video.category)?.label || video.category}
                          </Badge>
                          {video.durationSeconds && (
                            <span className="text-[10px] text-muted-foreground">{video.durationSeconds}s</span>
                          )}
                          <span className="text-[10px] text-muted-foreground">#{video.displayOrder}</span>
                        </div>
                      </div>
                      <Switch
                        checked={video.isActive}
                        onCheckedChange={(checked) => toggleMutation.mutate({ id: video.id, isActive: checked })}
                        data-testid={`switch-video-active-${video.id}`}
                      />
                    </div>
                    {video.description && (
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{video.description}</p>
                    )}
                    {video.ctaText && (
                      <div className="flex items-center gap-1 mt-1">
                        <ExternalLink className="w-3 h-3 text-muted-foreground" />
                        <span className="text-[10px] text-muted-foreground">{video.ctaText} → {video.ctaLink}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-1 mt-2">
                      <Dialog open={editingVideo?.id === video.id} onOpenChange={(open) => { if (!open) setEditingVideo(undefined); }}>
                        <DialogTrigger asChild>
                          <Button size="sm" variant="ghost" onClick={() => setEditingVideo(video)} data-testid={`button-edit-video-${video.id}`}>
                            <Pencil className="w-3 h-3 mr-1" />
                            Edit
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-lg">
                          <DialogHeader>
                            <DialogTitle>Edit Video</DialogTitle>
                          </DialogHeader>
                          {editingVideo?.id === video.id && (
                            <VideoForm key={video.id} video={video} onClose={() => setEditingVideo(undefined)} />
                          )}
                        </DialogContent>
                      </Dialog>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-destructive"
                        onClick={() => {
                          if (confirm("Delete this video?")) {
                            deleteMutation.mutate(video.id);
                          }
                        }}
                        data-testid={`button-delete-video-${video.id}`}
                      >
                        <Trash2 className="w-3 h-3 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!previewVideo} onOpenChange={(open) => { if (!open) setPreviewVideo(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{previewVideo?.title}</DialogTitle>
          </DialogHeader>
          {previewVideo && (
            <div className="aspect-video rounded-md overflow-hidden bg-black">
              <video
                className="w-full h-full"
                src={previewVideo.videoUrl}
                controls
                autoPlay
                data-testid="video-preview-player"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
